import { ConnectProto } from "@racoon/api";
import { fromTimestamp } from "@racoon/lib";
import { nearestOnSite, PHOTO_OFF_SITE_M } from "~/features/map-view/model/helpers/photo-points";

/**
 * What the track says about a job, beside its photos: how close the truck came to the site
 * while the job ran, whether it stood there, and how full the containers were. Each part is
 * null or empty when its data is missing, so the report shows only what can be backed.
 *
 * The photos' own coordinates are not used: they proved unreliable on real reports.
 */

/** How far from the site the truck may be before it is flagged; AIS draws ~50 m around a site. */
export const SITE_REACH_M = PHOTO_OFF_SITE_M;

/**
 * Slack around the job's fact window: the driver taps "started" / "done" in the app, which
 * can trail the truck's arrival and lead its departure by a few minutes.
 */
export const JOB_WINDOW_MARGIN_MS = 5 * 60_000;

export type TruckNearSite = {
  distanceM: number;
  /** When the truck came closest. */
  at: Date;
  offSite: boolean;
};

export type SiteStop = { startAt: Date; endAt: Date; durationSeconds: number };

export type ContainerFill = {
  /** One fill per container, 1 = full. */
  coefficients: number[];
  factVolume?: number;
  plannedVolume?: number;
};

type TrackPoint = { timestamp: Date; lat: number; lon: number };
type Stop = { lat: number; lon: number; startAt: Date; endAt: Date; durationSeconds: number };

const siteOf = (job: Pick<ConnectProto.Job, "pickupLocation">) => {
  const site = job.pickupLocation;
  return site && (site.lat || site.lon || site.geometry) ? site : undefined;
};

/**
 * The job's fact window widened by JOB_WINDOW_MARGIN_MS; a single fact time stands for both
 * ends. Null without either: a job never started has no window to check the track against.
 */
export function jobWindow(
  job: Pick<ConnectProto.Job, "factStartAt" | "factEndAt">,
): { from: number; to: number } | null {
  const start = fromTimestamp(job.factStartAt)?.getTime();
  const end = fromTimestamp(job.factEndAt)?.getTime();
  const from = start ?? end;
  const to = end ?? start;
  if (from === undefined || to === undefined) return null;
  return { from: from - JOB_WINDOW_MARGIN_MS, to: to + JOB_WINDOW_MARGIN_MS };
}

/**
 * The truck's closest approach to the site while the job ran, from the track. The photos'
 * own times are upload times, so the job's fact window is the moment to check, not theirs.
 */
export function truckNearSite(
  job: Pick<ConnectProto.Job, "pickupLocation" | "factStartAt" | "factEndAt">,
  track: TrackPoint[],
): TruckNearSite | null {
  const site = siteOf(job);
  const window = jobWindow(job);
  if (!site || !window) return null;

  let closest: TruckNearSite | null = null;
  for (const point of track) {
    const time = point.timestamp.getTime();
    if (time < window.from || time > window.to) continue;
    const nearest = nearestOnSite(point.lat, point.lon, site);
    if (nearest && (!closest || nearest.distanceM < closest.distanceM)) {
      closest = {
        distanceM: nearest.distanceM,
        at: point.timestamp,
        offSite: nearest.distanceM > SITE_REACH_M,
      };
    }
  }
  return closest;
}

/** GPS stops at the site that overlap the job's window, earliest first. */
export function siteStops(
  job: Pick<ConnectProto.Job, "pickupLocation" | "factStartAt" | "factEndAt">,
  stops: Stop[],
): SiteStop[] {
  const site = siteOf(job);
  const window = jobWindow(job);
  if (!site || !window) return [];

  return stops
    .filter((stop) => {
      if (stop.endAt.getTime() < window.from || stop.startAt.getTime() > window.to) return false;
      const nearest = nearestOnSite(stop.lat, stop.lon, site);
      return nearest !== null && nearest.distanceM <= SITE_REACH_M;
    })
    .map(({ startAt, endAt, durationSeconds }) => ({ startAt, endAt, durationSeconds }))
    .sort((a, b) => a.startAt.getTime() - b.startAt.getTime());
}

/**
 * The job as its report shows it. GovDash.Routes loads a route's jobs without their service
 * provider, so the MVO is taken from the route's truck, as the truck list names it.
 */
export function jobForReport(
  job: ConnectProto.Job,
  routes: Pick<ConnectProto.Route, "id" | "driverOnShift">[],
): ConnectProto.Job {
  if (job.serviceProvider) return job;
  // Ids may arrive as strings: the base query turns response int64s into them.
  const route = routes.find((candidate) => String(candidate.id) === String(job.routeId));
  const organization = route?.driverOnShift?.truck?.ownerOrganization;
  return organization ? { ...job, serviceProvider: organization } : job;
}

const round3 = (value: number) => Math.round(value * 1000) / 1000;

/**
 * How full the job's containers were, by the rules of admin's photo report
 * (apps/admin/src/features/photo-report-modal/model/container-volume.ts and
 * use-container-note.ts): without reported fills each planned container counts as full, and
 * the fact volume is Σfill × the container's volume unless the job reports one. Null for a job
 * with no containers to speak of.
 */
export function containerFill(
  job: Pick<
    ConnectProto.Job,
    | "containerCoefficients"
    | "numOfContainer"
    | "factTotalVolume"
    | "containerGroup"
    | "containerType"
  >,
): ContainerFill | null {
  const plannedCount = job.containerGroup?.numberOfContainers;
  const reported =
    (job.containerCoefficients?.length ?? 0) > 0 ||
    !!job.numOfContainer ||
    job.factTotalVolume != null;
  if (!reported && !plannedCount) return null;

  const coefficients =
    (job.containerCoefficients?.length ?? 0) > 0
      ? [...job.containerCoefficients]
      : Array.from({ length: plannedCount || Math.round(job.numOfContainer ?? 0) }, () => 1);

  const volume = job.containerGroup?.containerType?.volume ?? job.containerType?.volume;
  const perContainer = volume && volume > 0 ? volume : undefined;
  const estimated = perContainer
    ? round3(coefficients.reduce((sum, fill) => sum + fill, 0) * perContainer)
    : undefined;

  return {
    coefficients,
    factVolume: job.factTotalVolume ?? estimated,
    plannedVolume: perContainer && plannedCount ? round3(perContainer * plannedCount) : undefined,
  };
}
